import type { components } from '@social-studio/api-client';
import { api } from '../../lib/api';
import type { AssistantContext } from './route-context';

export type AssistantEstimate = components['schemas']['AssistantEstimateDto'];
export type ResolvedContext = components['schemas']['AssistantContextResolvedDto'];
export type AssistantAction = components['schemas']['AssistantActionDto'];
export type AssistantTurn = components['schemas']['AssistantTurnPollDto'];
export type AssistantConversation = components['schemas']['AssistantConversationDto'];
export type AssistantConversationDetail = components['schemas']['AssistantConversationDetailDto'];
export type AssistantStarted = components['schemas']['AssistantStartedDto'];

/** A failed request: the HTTP status (0 = the request did not arrive) and the problem type slug when the API named one. */
export class AssistantRequestError extends Error {
  constructor(
    readonly status: number,
    readonly problem: string | null = null,
  ) {
    super(`assistant request answered ${status}${problem ? ` ${problem}` : ''}`);
  }
}

const problemOf = (error: unknown): string | null => {
  const type = (error as { type?: unknown } | null | undefined)?.type;
  return typeof type === 'string' ? (type.split('/').pop() ?? null) : null;
};

async function call<T>(request: () => Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let result;
  try {
    result = await request();
  } catch {
    throw new AssistantRequestError(0);
  }
  if (result.response.ok && result.data !== undefined) return result.data;
  throw new AssistantRequestError(result.response.status, problemOf(result.error));
}

/** A fresh key per attempt: a repeat of the same attempt returns the first answer, a retry is a new turn. */
export const newIdempotencyKey = (): string => globalThis.crypto.randomUUID();

export const fetchEstimate = (): Promise<AssistantEstimate> => call(() => api.GET('/v1/assistant/estimate'));

export function fetchContext(context: AssistantContext): Promise<ResolvedContext> {
  return call(() => api.GET('/v1/assistant/context', { params: { query: { kind: context.kind, id: context.id, month: context.month, property_id: context.property_id } } }));
}

export interface ConversationPage {
  readonly data: AssistantConversation[];
  readonly next_cursor: string | null;
}

/** One page of the member's conversations; `cursor` is the previous page's `next_cursor`. */
export async function fetchConversations(cursor?: string): Promise<ConversationPage> {
  const page = await call(() => api.GET('/v1/assistant/conversations', { params: { query: { limit: 30, cursor } } }));
  return { data: page.data, next_cursor: page.next_cursor ?? null };
}

/**
 * Whether the assistant is offered to this member (spec 110 section 4.15.6, AC-110-141). The probe is the
 * conversation list: its guard (`content:read`) is the spec's user set, so a viewer or reviewer reaches the
 * tenant switch like an editor does (the `ai:run` estimate would stop them at the permission guard first).
 * `assistant-disabled` and `permission-denied` mean not offered; a request that did not get through
 * (network, 5xx) leaves the entry offered, and the panel shows its own error state.
 */
export async function fetchAvailability(): Promise<boolean> {
  try {
    await call(() => api.GET('/v1/assistant/conversations', { params: { query: { limit: 1 } } }));
    return true;
  } catch (error) {
    if (error instanceof AssistantRequestError && (error.problem === 'assistant-disabled' || error.problem === 'permission-denied')) return false;
    return true;
  }
}

export const fetchConversation = (id: string): Promise<AssistantConversationDetail> =>
  call(() => api.GET('/v1/assistant/conversations/{id}', { params: { path: { id } } }));

export const startConversation = (text: string, context: AssistantContext, key: string): Promise<AssistantStarted> =>
  call(() => api.POST('/v1/assistant/conversations', { params: { header: { 'Idempotency-Key': key } }, body: { text, context } }));

export const startTurn = (conversationId: string, text: string, context: AssistantContext, key: string) =>
  call(() => api.POST('/v1/assistant/conversations/{id}/turns', { params: { path: { id: conversationId }, header: { 'Idempotency-Key': key } }, body: { text, context } }));

export const fetchTurn = (conversationId: string, turnId: string): Promise<AssistantTurn> =>
  call(() => api.GET('/v1/assistant/conversations/{id}/turns/{turnId}', { params: { path: { id: conversationId, turnId } } }));

export const undoAction = (id: string, key: string): Promise<AssistantAction> =>
  call(() => api.POST('/v1/assistant/actions/{id}/undo', { params: { path: { id }, header: { 'Idempotency-Key': key } } }));
